import { ReactNode, useCallback, useMemo, useRef } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Stack } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridCellParams,
  GridRowHeightParams,
  GridRowParams,
  MuiEvent,
} from '@mui/x-data-grid-premium';

import { useModuleGroupRowColor } from '@/shared/lib/hooks/useModuleGroupRowColor';

import {
  ALL_COLUMNS_BUFFER_PX,
  DEFAULT_COLUMN_BUFFER_PX,
  ROW_OVERSCAN_PX,
} from './GeneratikTable.constants';
import {
  CONTEXT_MENU_ICON_SLOTS,
  DetailPanelCollapseIcon,
  DetailPanelExpandIcon,
} from './GeneratikTable.icons';
import { StyledGeneratikDataGrid, TableContainer, TablePaper } from './GeneratikTable.styles';
import GeneratikTableColumnMenu from './GeneratikTableColumnMenu';
import { useGeneratikTableContext } from './GeneratikTableContext';
import {
  buildDefaultDataGridSx,
  getFooterRowSx,
  getTableContainerSx,
  getTablePaperSx,
  predefinedFiltersWrapperSx,
} from './GeneratikTableDefaultDisplay.styles';
import GeneratikTableFilterPanel from './GeneratikTableFilterPanel';
import GeneratikTableFooter from './GeneratikTableFooter';
import GeneratikTableHeader from './GeneratikTableHeader';
import GeneratikTableHeadersLoadingPlaceholder, {
  shouldShowHeadersLoadingPlaceholder,
} from './GeneratikTableHeadersLoadingPlaceholder';
import GeneratikTableListView from './GeneratikTableListView';
import GeneratikTableToolbar from './GeneratikTableToolbar';
import SortPriorityBadge from './SortPriorityBadge';
import { TableContextMenu, useTableContextMenu } from './contextMenu';
import { getZoomedFontSize, getZoomedHeaderHeight, getZoomedRowHeight } from './contextMenu/zoom';
import useDataGridScrollbars from './hooks/useDataGridScrollbars';
import useDataGridVerticalScrollbar from './hooks/useDataGridVerticalScrollbar';
import useDraggedColumnHighlight from './hooks/useDraggedColumnHighlight';
import useSelectionCheckboxColumns from './hooks/useSelectionCheckboxColumns';

const GeneratikTableDefaultDisplay = () => {
  const ctx = useGeneratikTableContext();
  const {
    t,
    fillHeight,
    height,
    radioSelection,
    sx,
    ToolbarComponent,
    NoRowsOverlay,
    RadioSlot,
    CheckboxSlot,
  } = ctx;
  const tablePaperRef = useRef<HTMLDivElement>(null);
  const scrollbarKey =
    ctx.dataGridKey != null ? `${ctx.viewMode}-${ctx.dataGridKey}` : ctx.viewMode;
  const isGridView = ctx.viewMode !== 'list' || !ctx.renderListViewRow;
  useDataGridScrollbars(tablePaperRef, scrollbarKey, isGridView);
  useDataGridVerticalScrollbar(
    tablePaperRef,
    scrollbarKey,
    isGridView && !ctx.hideVerticalScrollbar,
  );
  const isDraggingColumn = useDraggedColumnHighlight(tablePaperRef, ctx.apiRef);
  const gridColumns = useSelectionCheckboxColumns();

  const listViewSortModel = ctx.sortingMode === 'server' ? undefined : ctx.sortModel;
  const listViewSortColumns = ctx.sortingMode === 'server' ? undefined : ctx.processedColumns;

  const contextMenu = useTableContextMenu({
    apiRef: ctx.apiRef,
    rows: ctx.rows,
    getRowId: ctx.getRowId || ctx.defaultGetRowId,
    groupByField: ctx.groupByField,
    columnRawFieldMap: ctx.columnRawFieldMap,
  });

  const dataGridSlots = useMemo(
    () => ({
      toolbar: ToolbarComponent,
      noRowsOverlay: NoRowsOverlay,
      baseCheckbox: radioSelection ? RadioSlot : CheckboxSlot,
      baseTooltip: ({ children }: { children: ReactNode }) => <>{children}</>,
      baseBadge: SortPriorityBadge,
      columnMenu: GeneratikTableColumnMenu,
      ...CONTEXT_MENU_ICON_SLOTS,
      treeDataExpandIcon: KeyboardArrowDownIcon,
      treeDataCollapseIcon: KeyboardArrowUpIcon,
      detailPanelExpandIcon: DetailPanelExpandIcon,
      detailPanelCollapseIcon: DetailPanelCollapseIcon,
    }),
    [ToolbarComponent, NoRowsOverlay, radioSelection, RadioSlot, CheckboxSlot],
  );

  const dataGridSlotProps = useMemo(
    () => ({
      toolbar: {
        showQuickFilter: true,
        quickFilterProps: { debounceMs: 300 },
      },
    }),
    [],
  );

  const dataGridLocaleText = useMemo(
    () => ({
      toolbarFilters: t('filter'),
      toolbarColumns: t('Columns'),
      toolbarExport: t('buttons.export'),
      noRowsLabel: t('No data available'),
      MuiTablePagination: {
        labelRowsPerPage: t('Rows per page'),
      },
    }),
    [t],
  );

  const dataGridSx = useMemo(
    () => ({
      ...buildDefaultDataGridSx({
        fillHeight,
        height,
        radioSelection,
        hasRowGrouping: Boolean(ctx.groupByField),
        consumerSx: sx,
      }),
      fontSize: getZoomedFontSize(ctx.zoomLevel),
      '--gt-font-size': getZoomedFontSize(ctx.zoomLevel),
      '--gt-row-height': `${ctx.rowHeight ?? getZoomedRowHeight(ctx.zoomLevel)}px`,
      '--gt-header-height': `${getZoomedHeaderHeight(ctx.zoomLevel)}px`,
    }),
    [ctx.groupByField, ctx.rowHeight, ctx.zoomLevel, fillHeight, height, radioSelection, sx],
  );

  const moduleGroupRowColor = useModuleGroupRowColor();

  const tablePaperSx = useMemo(
    () => ({
      ...getTablePaperSx(ctx.fillHeight, ctx.tableHeight),
      '--gt-group-row-bg': moduleGroupRowColor.base,
      '--gt-group-row-bg-hover': moduleGroupRowColor.hover,
    }),
    [ctx.fillHeight, ctx.tableHeight, moduleGroupRowColor.base, moduleGroupRowColor.hover],
  );

  const rowSelectionPropagation = useMemo(() => ({ descendants: false, parents: false }), []);

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      const rowNode = ctx.apiRef.current?.getRowNode(params.id);
      const hasChildren = (rowNode?.children?.length ?? 0) > 0;
      const depth = rowNode?.depth ?? 0;
      if (rowNode?.type === 'group' && ctx.groupByField && depth === 0 && hasChildren) {
        return getZoomedHeaderHeight(ctx.zoomLevel);
      }
      return null;
    },
    [ctx.apiRef, ctx.groupByField, ctx.zoomLevel],
  );

  const getDetailPanelContent = useMemo(
    () =>
      ctx.renderDetailPanel
        ? (params: GridRowParams) => {
            if (ctx.getRowCanExpand && !ctx.getRowCanExpand(params)) {
              return undefined;
            }
            return ctx.renderDetailPanel!(params);
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.getRowCanExpand, ctx.renderDetailPanel],
  );

  const getDetailPanelHeight = useMemo(
    () =>
      ctx.renderDetailPanel
        ? typeof ctx.detailPanelHeight === 'function'
          ? ctx.detailPanelHeight
          : () => ctx.detailPanelHeight as number | 'auto'
        : undefined,
    [ctx.detailPanelHeight, ctx.renderDetailPanel],
  );

  const onCellClick = useCallback(
    (params: GridCellParams, event: MuiEvent) => {
      if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
        const rowNode = params.rowNode as { isAutoGenerated?: boolean } | undefined;
        const isSyntheticGroup = !!rowNode?.isAutoGenerated;
        if (isSyntheticGroup) {
          return;
        }
        if (ctx.treeDataEnabled || ctx.radioSelection) {
          event.defaultMuiPrevented = true;
          ctx.toggleRowSelection(params.id);
          return;
        }
      }
      if (ctx.singleClickEdit && params.isEditable && params.cellMode === 'view') {
        ctx.apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.treeDataEnabled, ctx.radioSelection, ctx.singleClickEdit, ctx.toggleRowSelection],
  );

  return (
    <TableContainer
      fillHeight={ctx.fillHeight}
      sx={getTableContainerSx({
        fillHeight: ctx.fillHeight,
        noTopPadding: ctx.noTopPadding,
        noPadding: ctx.noPadding,
        height: ctx.height,
      })}
    >
      {ctx.showCustomToolbar &&
        !ctx.hideHeader &&
        (ctx.title || (ctx.activeFilters?.length ?? 0) > 0) && (
          <GeneratikTableHeader
            title={ctx.title ?? ''}
            titleIcon={ctx.titleIcon}
            filteredCount={ctx.hideCount ? undefined : ctx.totalCount}
            totalCount={ctx.hideCount ? undefined : ctx.totalCount}
            filters={ctx.activeFilters}
            onFilterRemove={ctx.onFilterRemove}
            onFilterClick={ctx.handleFilterClick}
            hasBackButton={ctx.hasBackButton}
          />
        )}

      {ctx.showCustomToolbar && (
        <GeneratikTableToolbar
          leftButtons={ctx.leftButtons}
          rightButtons={ctx.rightButtons}
          showSearch={ctx.showSearch}
          searchValue={ctx.searchValue}
          onSearchChange={ctx.onSearchChange}
          searchPlaceholder={ctx.searchPlaceholder}
          showColumns={ctx.showColumns}
          disableColumnsButton={ctx.disableColumnsButton}
          columns={ctx.processedColumns}
          visibleColumns={ctx.columnVisibilityModel}
          campaignColumnVisibility={ctx.campaignColumnVisibility}
          onColumnVisibilityChange={ctx.onColumnVisibilityChange}
          showSort={!!ctx.onSortModelChange}
          sortOptions={ctx.sortOptions}
          sortActions={ctx.sortActions}
          disableSortButton={ctx.disableSortButton}
          showGroup={!!ctx.onGroupChange}
          groupOptions={ctx.groupOptions}
          currentGroup={ctx.groupByField ?? undefined}
          onGroupChange={ctx.onGroupChange}
          showFilter={(ctx.filterFields?.length ?? 0) > 0 || (ctx.quickFilters?.length ?? 0) > 0}
          onFilterClick={ctx.handleFilterClick}
          filterButtonRef={ctx.filterButtonRef}
          showViewToggle={!!ctx.renderListViewRow}
          viewMode={ctx.viewMode}
          onViewModeChange={ctx.onViewModeChange}
          disableGroupButton={ctx.disableGroupButton}
          showVariantToggle={ctx.hasTimelineColumns}
          variant={ctx.variant}
          onVariantChange={ctx.onVariantChange}
          timelineGranularity={ctx.timelineGranularity}
          onTimelineGranularityChange={ctx.onTimelineGranularityChange}
          effectiveTimelineAxis={ctx.effectiveTimelineAxis}
          seamless={ctx.seamless}
          plain={ctx.plainToolbar}
          startContent={ctx.toolbarStartContent}
        />
      )}

      {ctx.predefinedFilters && <Box sx={predefinedFiltersWrapperSx}>{ctx.predefinedFilters}</Box>}

      <TablePaper
        ref={tablePaperRef}
        data-testid="table-component"
        noPaper={ctx.viewMode === 'list' && !!ctx.renderListViewRow}
        seamless={ctx.seamless}
        sx={tablePaperSx}
        onContextMenu={ctx.enableContextMenu && isGridView ? contextMenu.open : undefined}
      >
        {ctx.viewMode === 'list' && ctx.renderListViewRow ? (
          <GeneratikTableListView
            tableId={ctx.tableId}
            rows={ctx.rows}
            renderListViewRow={ctx.renderListViewRow}
            openFilters={ctx.handleFilterClick}
            getRowId={ctx.getRowId || ctx.defaultGetRowId}
            emptyStateMessage={ctx.emptyStateMessage}
            isError={ctx.isError}
            onRetry={ctx.onRetry}
            loading={ctx.loading}
            selectionModel={ctx.selectionModel}
            onToggleRowSelection={ctx.toggleRowSelection}
            onRowSelectionModelChange={ctx.handleRowSelectionChange}
            scrollable={ctx.fillHeight}
            groupByField={ctx.groupByField}
            groupOptions={ctx.groupOptions}
            getGroupItemPrice={ctx.getGroupItemPrice}
            priceForPeriod={ctx.groupPriceForPeriod}
            currency={ctx.currency}
            isRowSelectable={ctx.isRowSelectable}
            isChildRow={ctx.isChildRow}
            sortModel={listViewSortModel}
            sortColumns={listViewSortColumns}
          />
        ) : shouldShowHeadersLoadingPlaceholder(ctx) ? (
          <GeneratikTableHeadersLoadingPlaceholder />
        ) : (
          <StyledGeneratikDataGrid
            key={ctx.dataGridKey}
            ref={ctx.gridRootRef}
            autoHeight={ctx.height === 'auto'}
            apiRef={ctx.apiRef}
            rows={ctx.rows}
            columns={gridColumns}
            rowCount={ctx.rowCount ?? ctx.rows.length}
            getRowId={ctx.getRowId || ctx.defaultGetRowId}
            getRowClassName={ctx.getRowClassName}
            getCellClassName={ctx.getCellClassName}
            loading={ctx.loading}
            disableRowSelectionOnClick={ctx.disableRowSelectionOnClick}
            rowSelectionModel={ctx.selectionModel}
            onRowSelectionModelChange={ctx.handleRowSelectionChange}
            keepNonExistentRowsSelected
            columnVisibilityModel={ctx.effectiveColumnVisibilityModel}
            onColumnVisibilityModelChange={ctx.onColumnVisibilityModelChange}
            pagination
            paginationMode={ctx.paginationMode}
            paginationModel={ctx.paginationModel}
            onPaginationModelChange={ctx.onPaginationModelChange}
            pageSizeOptions={ctx.pageSizeOptions}
            hideFooter
            sortingMode={ctx.sortingMode}
            sortModel={ctx.sortModel}
            onSortModelChange={ctx.disableSortButton ? undefined : ctx.onSortModelChange}
            onColumnHeaderClick={ctx.disableSortButton ? undefined : ctx.onColumnHeaderClick}
            filterMode={ctx.filterMode}
            filterModel={ctx.filterModel}
            onFilterModelChange={ctx.onFilterModelChange}
            filterDebounceMs={300}
            treeData={ctx.treeDataEnabled}
            getTreeDataPath={
              ctx.treeDataEnabled
                ? (ctx.effectiveGetTreeDataPath as
                    | ((row: (typeof ctx.rows)[number]) => string[])
                    | undefined)
                : undefined
            }
            groupingColDef={ctx.groupingColDef}
            defaultGroupingExpansionDepth={ctx.defaultGroupingExpansionDepth ?? -1}
            isGroupExpandedByDefault={ctx.isGroupExpandedByDefault}
            rowSelectionPropagation={rowSelectionPropagation}
            onRowClick={ctx.handleRowClick}
            onRowDoubleClick={ctx.handleRowDoubleClick}
            onCellClick={onCellClick}
            processRowUpdate={ctx.processRowUpdate}
            disableColumnMenu={ctx.hideColumnMenu}
            disableColumnReorder={!ctx.isTimelineActive}
            disableColumnPinning={ctx.isTimelineActive}
            pinnedColumns={ctx.pinnedColumns}
            onPinnedColumnsChange={ctx.onPinnedColumnsChange}
            onColumnOrderChange={ctx.handleColumnOrderChange}
            onColumnWidthChange={ctx.handleColumnWidthChange}
            density={ctx.density}
            rowHeight={ctx.rowHeight ?? getZoomedRowHeight(ctx.zoomLevel)}
            getRowHeight={getRowHeight}
            columnHeaderHeight={getZoomedHeaderHeight(ctx.zoomLevel)}
            disableAggregation
            checkboxSelection={ctx.checkboxSelection || ctx.radioSelection}
            isRowSelectable={ctx.isRowSelectable}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            detailPanelExpandedRowIds={ctx.detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={ctx.onDetailPanelExpandedRowIdsChange}
            slots={dataGridSlots}
            slotProps={dataGridSlotProps}
            localeText={dataGridLocaleText}
            sx={dataGridSx}
            rowBufferPx={ROW_OVERSCAN_PX}
            columnBufferPx={isDraggingColumn ? ALL_COLUMNS_BUFFER_PX : DEFAULT_COLUMN_BUFFER_PX}
          />
        )}

        <Stack
          data-testid="table-footer"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={ctx.viewMode === 'list' && !!ctx.renderListViewRow ? '1rem' : 0}
          pl={ctx.viewMode === 'list' && ctx.renderListViewRow ? 0 : '0.5rem'}
          sx={getFooterRowSx(ctx.viewMode === 'list' && !!ctx.renderListViewRow)}
          onContextMenu={(e) => e.stopPropagation()}
        >
          <GeneratikTableFooter
            hidePagination={ctx.hidePagination}
            page={ctx.paginationModel.page}
            pageSize={ctx.paginationModel.pageSize}
            rowCount={ctx.rowCount ?? ctx.rows.length}
            pageSizeOptions={ctx.pageSizeOptions}
            onPageChange={ctx.handlePageChange}
            onPageSizeChange={ctx.handlePageSizeChange}
            pageTotal={ctx.pageTotal}
            pageSelected={ctx.pageSelected}
            spacesTotal={ctx.spacesTotal}
            productionTotal={ctx.productionTotal}
            grandTotal={ctx.grandTotal}
            selectedRowsCount={ctx.selectedRows.length}
            currency={ctx.currency}
          />
        </Stack>

        {ctx.enableContextMenu && isGridView && (
          <TableContextMenu state={contextMenu.state} onClose={contextMenu.close} />
        )}
      </TablePaper>

      {((ctx.filterFields?.length ?? 0) > 0 ||
        (ctx.quickFilters && ctx.quickFilters.length > 0)) && (
        <GeneratikTableFilterPanel
          anchorEl={ctx.filterAnchorEl}
          open={Boolean(ctx.filterAnchorEl)}
          onClose={ctx.handleFilterClose}
          fields={ctx.filterFields}
          values={ctx.filterValues}
          onChange={ctx.onFilterValuesChange}
          onApply={ctx.handleFilterApplyInternal}
          onReset={ctx.onFilterReset}
          predefinedFilters={ctx.quickFilters}
          campaignColumnVisibility={ctx.campaignColumnVisibility}
          initialFocusFieldId={ctx.focusFieldId}
        />
      )}
    </TableContainer>
  );
};

export default GeneratikTableDefaultDisplay;
